import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { fmtCurrency } from "@/lib/calc";
import { RefundDialog } from "@/components/booking/RefundDialog";
import { TakePaymentDialog } from "@/components/booking/TakePaymentDialog";
import { Receipt, Undo2, Send, Wallet } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS = {
  paid: ["Paid", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  owing: ["Owing", "bg-amber-50 text-amber-700 border-amber-200"],
  partially_refunded: ["Part refunded", "bg-amber-50 text-amber-700 border-amber-200"],
  refunded: ["Refunded", "bg-red-50 text-red-700 border-red-200"],
  voided: ["Voided", "bg-muted text-muted-foreground border-border"],
};

// Every invoice for a client (booking checkouts + quick sales) with partial refunds that don't require voiding.
export const ClientInvoices = ({ clientId, cur = "AUD", isAdmin }) => {
  const [rows, setRows] = useState(null);
  const [pay, setPay] = useState(null);
  const [take, setTake] = useState(null);
  const [checking, setChecking] = useState(false);
  const recheck = async () => {
    setChecking(true);
    try { const { data } = await api.post("/payments/reconcile"); toast.success(data.paid ? `Found ${data.paid} payment${data.paid === 1 ? "" : "s"} Stripe had taken — now applied` : `Checked ${data.checked} pending link${data.checked === 1 ? "" : "s"} — nothing new paid`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reach Stripe"); }
    finally { setChecking(false); }
  };
  const [sending, setSending] = useState("");
  const load = () => api.get(`/clients/${clientId}/sales`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { if (clientId) load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  const openRefund = async (r) => {
    try { const { data } = await api.get(`/sales/${r.id}/payment`); setPay(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load this invoice"); }
  };
  const resendLink = async (r) => {
    setSending(r.id);
    try {
      const { data } = await api.post(`/sales/${r.id}/pay-link`, {});
      toast.success(`Payment link texted · ${fmtCurrency(data.amount, cur, 2)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the link"); }
    setSending("");
  };
  if (!rows) return <p className="text-sm text-muted-foreground">Loading invoices…</p>;
  const total = rows.filter((r) => r.status !== "voided").reduce((s, r) => s + r.total_paid - r.refunded_amount, 0);
  const owing = rows.filter((r) => r.status !== "voided").reduce((s, r) => s + (r.amount_owing || 0), 0);
  return (
    <div className="space-y-3" data-testid="client-invoices">
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" />Invoices ({rows.length})</div>
        <div className="text-xs text-muted-foreground flex items-center gap-3">
          {owing > 0.005 && <span>Owing <span className="font-semibold text-amber-700 tabular-nums" data-testid="invoices-owing">{fmtCurrency(owing, cur, 2)}</span></span>}
          <span title="Money actually collected, after refunds. Credit applied isn't counted again — it was paid when the credit was bought.">Net paid <span className="font-semibold text-foreground tabular-nums" data-testid="invoices-net">{fmtCurrency(total, cur, 2)}</span></span>
          <button type="button" onClick={recheck} disabled={checking} className="text-xs text-primary hover:underline disabled:opacity-50" title="Client says they paid a link but it isn't showing? Ask Stripe again." data-testid="invoices-recheck-stripe">{checking ? "Checking Stripe…" : "Re-check Stripe"}</button>
        </div>
      </div>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">No invoices yet.</p>}
      <div className="divide-y rounded-xl border">
        {rows.map((r) => {
          const [label, cls] = STATUS[r.status] || STATUS.paid;
          return (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`invoice-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-mono text-xs text-muted-foreground">#{r.invoice_no}</span><span className="font-medium truncate">{r.service_name}</span><span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${cls}`} data-testid={`invoice-status-${r.id}`}>{label}</span></div>
                <div className="text-xs text-muted-foreground">
                  {fmt(r.created_at)} · {r.method_label}{r.refunded_amount > 0 ? ` · refunded ${fmtCurrency(r.refunded_amount, cur, 2)}` : ""}
                  {r.amount_owing > 0 ? ` · ${fmtCurrency(r.amount_owing, cur, 2)} owing` : ""}{r.link_paid > 0 ? ` · ${fmtCurrency(r.link_paid, cur, 2)} paid by link` : ""}
                  {r.credit_used > 0 ? <span data-testid={`invoice-credit-${r.id}`}> · {fmtCurrency(r.credit_used, cur, 2)} from credit</span> : null}
                  {(r.payments || []).filter((p) => p.kind === "later" && p.method !== "credit").map((p) => <span key={p.id}> · {fmtCurrency(p.amount, cur, 2)} {p.method.replace(/_/g, " ")} {fmt(p.at)}</span>)}
                </div>
              </div>
              <div className="font-semibold tabular-nums">{fmtCurrency(r.total_paid, cur, 2)}</div>
              {r.status !== "voided" && r.amount_owing > 0.005 && <Button size="sm" className="rounded-lg h-8" onClick={() => setTake(r)} data-testid={`invoice-take-payment-${r.id}`}><Wallet className="h-3.5 w-3.5 mr-1" />Take payment</Button>}
              {r.status !== "voided" && r.amount_owing > 0.005 && <Button size="sm" variant="outline" className="rounded-lg h-8" disabled={sending === r.id} onClick={() => resendLink(r)} data-testid={`invoice-resend-link-${r.id}`}><Send className="h-3.5 w-3.5 mr-1" />{sending === r.id ? "Sending…" : "Resend link"}</Button>}
              {isAdmin && r.status !== "voided" && r.refundable > 0.005 && <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => openRefund(r)} data-testid={`invoice-refund-${r.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" />Refund</Button>}
            </div>
          );
        })}
      </div>
      {take && <TakePaymentDialog sale={take} clientId={clientId} cur={cur} onClose={() => setTake(null)} onDone={() => { setTake(null); load(); }} />}
      {pay && <RefundDialog open={!!pay} onOpenChange={(o) => !o && setPay(null)} pay={pay} cur={cur} onDone={() => { setPay(null); toast.success("Refund recorded"); load(); }} />}
    </div>
  );
};
